import React, { useEffect, useState } from "react";
import MultiColumnOption from "web/react/components/select/multi-column-option";
import Select from "web/react/components/select/select";
import { Option } from "web/react/components/select/types";
import analytics from "web/script/analytics/analytics";
import { Features } from "web/types/analytics";
import { Schemas } from "web/types/examples/schemas";
import { LocalizedSize, Size } from "web/types/examples/size";
import {
    SelectableSchemaSizePickerOptionHeading,
    SizePickerOptionHeading,
    SizePickerOptionHeadingProps,
} from "./option-heading";

function compileSizeOptions(sizes: Size[]): Option[] {
    return sizes.map((size) => {
        return {
            selectedText: size.human_size,
            text: [size.human_size],
            value: size,
        };
    });
}

function compileLocalizedSizeOptions(sizes: LocalizedSize[], schema: string): Option[] {
    return sizes.map((size: LocalizedSize) => {
        const localizedSizeText = size.localized_sizes[schema].display_size;
        const retailerSizeText = size.retailer_size.display_size;
        let selectedText: string;

        if (localizedSizeText === retailerSizeText) {
            selectedText = localizedSizeText;
        } else {
            selectedText = `${localizedSizeText} / ${retailerSizeText}`;
        }

        return {
            selectedText,
            text: [localizedSizeText, retailerSizeText],
            value: size,
        };
    });
}

function createSelectableSchemaSizePickerOptionHeading(
    onSchemaSelect,
    schemas,
    selectedSchema
): (SizePickerOptionHeadingProps: SizePickerOptionHeadingProps) => JSX.Element {
    return function SelectableSchemaSizePickerOptionHeadingWrapper({
        onCloseButtonClick,
    }: SizePickerOptionHeadingProps): JSX.Element {
        return (
            <SelectableSchemaSizePickerOptionHeading
                onCloseButtonClick={onCloseButtonClick}
                onSchemaSelect={onSchemaSelect}
                schemas={schemas}
                selectedSchema={selectedSchema}
            />
        );
    };
}

export interface SizePickerProps {
    isExpanded: boolean;
    onSelect: (option: Option) => void;
    schemas: Schemas;
    setExpanded: (expanded: boolean) => void;
    sizes: Size[]; // supporting n-columns, envisioned n=[1,3]
    userDefaultSchema: string;
    defaultSelectText?: string;
    error?: boolean;
}

export default function SizePicker({
    isExpanded,
    onSelect,
    schemas,
    setExpanded,
    sizes,
    userDefaultSchema,
    defaultSelectText = "Select a size",
    error = false,
}: SizePickerProps): JSX.Element {
    // TODO: i18n
    const [selectedSchema, setSelectedSchema] = useState(userDefaultSchema);

    let sizeOptions: Option[];

    const localizedSizes = sizes.filter((size: Size) => {
        return size.localized_sizes;
    });

    const hasLocalizedSizes = !!selectedSchema && localizedSizes.length > 0;

    if (hasLocalizedSizes) {
        sizeOptions = compileLocalizedSizeOptions(
            localizedSizes as LocalizedSize[],
            selectedSchema
        );
    } else {
        sizeOptions = compileSizeOptions(sizes);
    }

    useEffect(() => {
        if (hasLocalizedSizes) {
            sizeOptions = compileLocalizedSizeOptions(
                localizedSizes as LocalizedSize[],
                selectedSchema
            );
        } else {
            sizeOptions = compileSizeOptions(sizes);
        }
    }, [selectedSchema]);

    function getFeatures(): Features {
        const features = {
            selectable_schema: hasLocalizedSizes,
        };
        return features;
    }

    function handleFirstExpand(): void {
        const category = "size_picker";
        const action = "first_expansion";
        const subtype = `checkout.${category}.${action}`;

        // Trigger an analytic to send:
        // - Num Sizes
        // - Which schema is initially selected
        // - What features the size picker contains
        analytics.event(category, action, "", false, null, subtype, {
            size_picker: {
                first_expansion: {
                    current_schema: selectedSchema,
                    features: getFeatures(),
                    sizes_available: localizedSizes.length || sizes.length,
                },
            },
        });
    }

    function handleSchemaSelect(event): void {
        const toSchema = event.target.value;
        const fromSchema = selectedSchema;

        setSelectedSchema(toSchema);

        const category = "size_picker";
        const action = "schema_change";
        const subType = `checkout.${category}.${action}`;

        // Track which schema the user was on, and what they switched to
        analytics.event(category, action, "", false, null, subType, {
            size_picker: {
                schema_change: {
                    from_schema: fromSchema,
                    to_schema: toSchema,
                },
            },
        });
    }

    return (
        <Select
            dataTestId="size-picker-select"
            defaultSelectText={defaultSelectText}
            error={error}
            isExpanded={isExpanded}
            onFirstExpand={handleFirstExpand}
            onSelect={onSelect}
            OptionComponent={MultiColumnOption}
            OptionHeadingComponent={
                hasLocalizedSizes
                    ? createSelectableSchemaSizePickerOptionHeading(
                          handleSchemaSelect,
                          schemas,
                          selectedSchema
                      )
                    : SizePickerOptionHeading
            }
            options={sizeOptions}
            setExpanded={setExpanded}
            showOnlyOptionByDefault={true}
        />
    );
}
